import React, { useMemo, useState } from "react"
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  CircularProgress,
  Tooltip
} from "@mui/material"
import { Delete, Save, Search, ViewAgenda, ViewModule } from "@mui/icons-material"
import { GallerySection } from "@/shared/types/product"
import { Prompt } from "@/shared/types/prompt"
import { MAX_CUSTOM_PROMPT_LENGTH } from "@/shared/utils/promptIds"
import _ from "lodash"
interface Props {
  section: GallerySection
  maxSelected: number
  prompts: Prompt[]
  loading: boolean
  error: string | null
  onPromptToggle: (sectionId: number, promptId: string) => void
  onSaveCustomPrompt: (promptText: string) => Promise<string>
  onDeleteCustomPrompt: (promptId: string) => Promise<void>
  customPromptSaving: boolean
  customPromptError: string | null
}

const PromptsPanel: React.FC<Props> = ({
  section,
  maxSelected,
  prompts,
  loading,
  error,
  onPromptToggle,
  onSaveCustomPrompt,
  onDeleteCustomPrompt,
  customPromptSaving,
  customPromptError
}) => {
  const [searchQuery, setSearchQuery] = useState("")
  const [selectedCategory, setSelectedCategory] = useState<string>("All")
  const [customPromptText, setCustomPromptText] = useState("")
  const [customPromptInputError, setCustomPromptInputError] = useState<string | null>(null)
  const [promptToDelete, setPromptToDelete] = useState<Prompt | null>(null)
  const [deletingPrompt, setDeletingPrompt] = useState(false)
  const [deletePromptError, setDeletePromptError] = useState<string | null>(null)
  const [isGridView, setIsGridView] = useState(false)
  const count = section.selectedPromptIds.length
  const query = searchQuery.trim().toLowerCase()

  const categories = useMemo(() => {
    const customCount = prompts.filter(p => p.source === "custom").length
    const counts = _.countBy(
      prompts.filter(p => p.source !== "custom" && p.categoryName),
      "categoryName"
    )
    return [
      { name: "All", count: prompts.length },
      { name: "Custom", count: customCount },
      ..._.map(counts, (count, name) => ({ name, count }))
    ]
  }, [prompts])
  const filteredPrompts = useMemo(() => {
    return prompts.filter(prompt => {
      if (selectedCategory === "Custom" && prompt.source !== "custom") return false
      if (selectedCategory !== "All" && selectedCategory !== "Custom" && prompt.categoryName !== selectedCategory) {
        return false
      }
      if (query && !prompt.name.toLowerCase().includes(query)) return false
      return true
    })
  }, [prompts, query, selectedCategory])

  const totalFiltered = useMemo(() => filteredPrompts.length, [filteredPrompts])

  const handleSaveCustomPrompt = async () => {
    const trimmedPrompt = customPromptText.trim()

    if (!trimmedPrompt) {
      setCustomPromptInputError("Please enter a prompt before saving.")
      return
    }

    if (trimmedPrompt.length > MAX_CUSTOM_PROMPT_LENGTH) {
      setCustomPromptInputError(`Prompt must be ${MAX_CUSTOM_PROMPT_LENGTH} characters or less.`)
      return
    }

    setCustomPromptInputError(null)

    try {
      const savedPromptId = await onSaveCustomPrompt(trimmedPrompt)
      setCustomPromptText("")

      if (!section.selectedPromptIds.includes(savedPromptId) && count < maxSelected) {
        onPromptToggle(section.id, savedPromptId)
      }
    } catch (saveError) {
      console.error("Failed to save custom prompt:", saveError)
    }
  }

  const renderPrompt = (prompt: Prompt) => {
    const isSelected = section.selectedPromptIds.includes(prompt.id)
    const isDisabled = !isSelected && count >= maxSelected
    const hasExampleImage = Boolean(prompt.exampleUrl)

    const previewContent = hasExampleImage ? (
      <Box
        component="img"
        src={prompt.exampleUrl}
        alt={`Preview for ${prompt.name}`}
        sx={{
          width: 260,
          maxWidth: "min(72vw, 260px)",
          height: 260,
          objectFit: "cover",
          borderRadius: 1
        }}
      />
    ) : (
      <Box />
    )

    return (
      <React.Fragment key={prompt.id}>
        <Divider sx={{ width: "100%", my: 0.5 }} />
        <Tooltip
          title={previewContent}
          arrow
          placement="right-start"
          disableHoverListener={!hasExampleImage}
          disableFocusListener={!hasExampleImage}
          disableTouchListener>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <FormControlLabel
              sx={{
                m: 0,
                width: "100%",
                opacity: isDisabled ? 0.45 : 1,
                "& .MuiFormControlLabel-label": { fontSize: 12, width: "100%" }
              }}
              control={
                <Checkbox
                  size="small"
                  checked={isSelected}
                  disabled={isDisabled}
                  onChange={() => onPromptToggle(section.id, prompt.id)}
                />
              }
              label={
                <Stack direction="row" alignItems="center" spacing={1}>
                  {hasExampleImage && (
                    <Box
                      component="img"
                      src={prompt.exampleUrl}
                      alt={`Example for ${prompt.name}`}
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: 0.75,
                        objectFit: "cover",
                        flexShrink: 0
                      }}
                    />
                  )}
                  <Typography component="span" fontSize={12}>
                    {prompt.name}
                  </Typography>
                </Stack>
              }
            />
            {prompt.source === "custom" && (
              <IconButton
                size="small"
                aria-label={`Delete prompt ${prompt.name}`}
                onClick={() => {
                  setPromptToDelete(prompt)
                  setDeletePromptError(null)
                }}>
                <Delete fontSize="small" />
              </IconButton>
            )}
          </Stack>
        </Tooltip>
      </React.Fragment>
    )
  }

  const renderPromptGridCard = (prompt: Prompt) => {
    const isSelected = section.selectedPromptIds.includes(prompt.id)
    const isDisabled = !isSelected && count >= maxSelected
    const hasExampleImage = Boolean(prompt.exampleUrl)

    return (
      <Box
        key={prompt.id}
        sx={{
          border: theme => `1px solid ${theme.palette.divider}`,
          borderRadius: 1.5,
          overflow: "hidden",
          opacity: isDisabled ? 0.45 : 1,
          backgroundColor: "background.paper"
        }}>
        <Box
          sx={{
            position: "relative",
            width: "100%",
            aspectRatio: "1 / 1",
            backgroundColor: "action.hover"
          }}>
          {hasExampleImage ? (
            <Box
              component="img"
              src={prompt.exampleUrl}
              alt={`Example for ${prompt.name}`}
              sx={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "text.secondary",
                fontSize: 12,
                px: 1,
                textAlign: "center"
              }}>
              No image
            </Box>
          )}

          <Box sx={{ position: "absolute", top: 6, right: 6, display: "flex", gap: 0.5 }}>
            <Checkbox
              size="small"
              checked={isSelected}
              disabled={isDisabled}
              onChange={() => onPromptToggle(section.id, prompt.id)}
              sx={{ p: 0.5, backgroundColor: "rgba(255,255,255,0.8)", borderRadius: 1 }}
            />
            {prompt.source === "custom" && (
              <IconButton
                size="small"
                aria-label={`Delete prompt ${prompt.name}`}
                onClick={() => {
                  setPromptToDelete(prompt)
                  setDeletePromptError(null)
                }}
                sx={{ backgroundColor: "rgba(255,255,255,0.8)" }}>
                <Delete fontSize="small" />
              </IconButton>
            )}
          </Box>
        </Box>

        <Box px={1} py={0.75}>
          <Typography
            fontSize={12}
            lineHeight={1.3}
            sx={{ display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
            {prompt.name}
          </Typography>
        </Box>
      </Box>
    )
  }

  const handleCloseDeleteDialog = () => {
    if (deletingPrompt) return
    setPromptToDelete(null)
    setDeletePromptError(null)
  }

  const handleConfirmDelete = async () => {
    if (!promptToDelete) return
    setDeletingPrompt(true)
    setDeletePromptError(null)
    try {
      await onDeleteCustomPrompt(promptToDelete.id)
      setPromptToDelete(null)
    } catch (deleteError) {
      console.error("Failed to delete custom prompt:", deleteError)
      setDeletePromptError("Failed to delete prompt. Please try again.")
    } finally {
      setDeletingPrompt(false)
    }
  }

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
        <Typography variant="subtitle2" fontWeight="bold">
          AI Image Prompts
        </Typography>
        <Typography variant="caption" color={count === maxSelected ? "primary.main" : "text.secondary"}>
          {count} / {maxSelected} selected
        </Typography>
      </Box>

      <Typography variant="body2" fontSize={12} color="text.secondary" mb={1.5}>
        Choose up to {maxSelected} prompts to generate up to {maxSelected} images for this product.
      </Typography>

      <TextField
        fullWidth
        multiline
        minRows={3}
        maxRows={6}
        slotProps={{
          input: {
            endAdornment: _.isEmpty(customPromptText.trim()) ? null : customPromptSaving ? (
              <CircularProgress size={20} />
            ) : (
              <InputAdornment position="end">
                <IconButton edge="end" onClick={handleSaveCustomPrompt} disabled={customPromptSaving}>
                  <Save fontSize="small" />
                </IconButton>
              </InputAdornment>
            )
          }
        }}
        label="Write your own prompt"
        placeholder="Describe the image you want to generate, then save it for reuse."
        size="small"
        value={customPromptText}
        onChange={e => {
          setCustomPromptText(e.target.value)
          if (customPromptInputError) {
            setCustomPromptInputError(null)
          }
        }}
        error={Boolean(customPromptInputError || customPromptError)}
        helperText={
          customPromptInputError ||
          customPromptError ||
          `Saved prompts are private to your account. Max ${MAX_CUSTOM_PROMPT_LENGTH} characters.`
        }
        sx={{ mb: 1 }}
      />
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 1.5 }}>
        {categories.map(({ name, count }) => {
          const isSelected = selectedCategory === name
          return (
            <Chip
              key={name}
              label={`${name} ${count}`}
              size="small"
              clickable
              color={isSelected ? "primary" : "default"}
              variant={isSelected ? "filled" : "outlined"}
              onClick={() => setSelectedCategory(name)}
              sx={{
                fontWeight: isSelected ? 600 : 400,
                borderRadius: 1.5,
                transition: "background-color 20ms ease, color 20ms ease",
                "& .MuiChip-label": { px: 1.25 }
              }}
            />
          )
        })}
      </Box>

      <TextField
        fullWidth
        placeholder="Search prompts…"
        size="small"
        value={searchQuery}
        onChange={e => setSearchQuery(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search fontSize="small" />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <Tooltip title={isGridView ? "Switch to list view" : "Switch to grid view"}>
                <IconButton size="small" onClick={() => setIsGridView(prev => !prev)}>
                  {isGridView ? <ViewAgenda fontSize="small" /> : <ViewModule fontSize="small" />}
                </IconButton>
              </Tooltip>
            </InputAdornment>
          )
        }}
        sx={{ mb: 1.5 }}
      />

      <Box sx={{ overflowY: "auto", maxHeight: 585, pb: 1 }}>
        {loading ? (
          <Typography fontSize={12} color="text.secondary">
            Loading prompts…
          </Typography>
        ) : error ? (
          <Typography fontSize={12} color="error.main">
            {error}
          </Typography>
        ) : totalFiltered === 0 ? (
          <Typography fontSize={12} color="text.secondary">
            No prompts match your search.
          </Typography>
        ) : isGridView ? (
          <Box
            sx={{
              display: "grid",
              gap: 1,
              gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))"
            }}>
            {filteredPrompts.map(renderPromptGridCard)}
          </Box>
        ) : (
          <Stack>{filteredPrompts.map(renderPrompt)}</Stack>
        )}
      </Box>

      <Dialog
        open={Boolean(promptToDelete)}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="delete-prompt-dialog-title">
        <DialogTitle id="delete-prompt-dialog-title">Delete prompt</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete &quot;{promptToDelete?.name || "this prompt"}&quot;? This action cannot be
            undone.
          </DialogContentText>
          {deletePromptError && (
            <Typography fontSize={12} color="error.main" mt={1}>
              {deletePromptError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} disabled={deletingPrompt}>
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained" disabled={deletingPrompt}>
            {deletingPrompt ? "Deleting…" : "Yes, Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default PromptsPanel
